import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';

const RefundPage = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 bg-gray-50" style={{ paddingTop: '120px', paddingBottom: '80px' }}>
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 sm:p-12">
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-8">Refund Policy</h1>
            <p className="text-sm text-gray-500 mb-8">Last updated: February 26, 2026</p>

            <div className="prose prose-gray max-w-none">
              <section className="mb-8">
                <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-6">
                  <h2 className="text-xl font-semibold text-green-800 mb-2">No Win, No Fee Guarantee</h2>
                  <p className="text-green-700">
                    Our services operate on a strict "No Win, No Fee" basis. If your claim is unsuccessful, 
                    you pay absolutely nothing. This is our core promise to you.
                  </p>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">1. Our Fee Structure</h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  AirDelayClaim charges a success fee only when your compensation claim is approved and paid:
                </p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2">
                  <li><strong>Success Fee:</strong> 25% of the compensation amount received</li>
                  <li><strong>VAT:</strong> Applicable VAT will be added to the success fee</li>
                  <li><strong>No Hidden Costs:</strong> No registration, processing, or administration fees</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">2. When You Pay Nothing</h2>
                <p className="text-gray-600 leading-relaxed mb-4">You will not be charged in the following situations:</p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2">
                  <li>Your claim is rejected by the airline</li>
                  <li>Your flight is not eligible for compensation</li>
                  <li>The airline successfully defends against the claim (extraordinary circumstances)</li>
                  <li>You withdraw your claim before any compensation is received</li>
                  <li>Legal proceedings are unsuccessful</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">3. Payment Process</h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  When your claim is successful:
                </p>
                <ol className="list-decimal pl-6 text-gray-600 space-y-2">
                  <li>The airline pays the compensation to FlightClaim</li>
                  <li>We deduct our success fee (25% + VAT) from the total</li>
                  <li>The remaining amount is transferred to your bank account</li>
                  <li>You receive a detailed breakdown of all deductions</li>
                </ol>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">4. Refund Eligibility</h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Since we operate on a "No Win, No Fee" basis, refunds typically do not apply. However, 
                  in exceptional circumstances, you may be eligible for a refund:
                </p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2">
                  <li>If we charged fees in error</li>
                  <li>If duplicate payments were processed</li>
                  <li>If there was a miscalculation in the fee deduction</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">5. Cancellation Rights</h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  You have the right to cancel your claim at any time before compensation is received:
                </p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2">
                  <li><strong>Within 14 days:</strong> Full cancellation with no questions asked</li>
                  <li><strong>After 14 days:</strong> Cancellation possible, but we may have already incurred costs</li>
                  <li><strong>After airline payment:</strong> Fees apply as service has been rendered</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">6. How to Request a Refund</h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  If you believe you are entitled to a refund, please contact us:
                </p>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-gray-700 mb-2"><strong>Email:</strong> info@airdelayclaim.com</p>
                  <p className="text-gray-700 mb-2"><strong>Subject Line:</strong> Refund Request - [Your Claim Reference]</p>
                  <p className="text-gray-700"><strong>Response Time:</strong> Within 5 business days</p>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">7. Refund Processing</h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Approved refunds will be processed within 14 business days to the original payment method. 
                  You will receive confirmation once the refund has been initiated.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">8. Disputes</h2>
                <p className="text-gray-600 leading-relaxed">
                  If you have a dispute regarding fees or refunds, please contact our customer service team at{' '}
                  <a href="mailto:info@airdelayclaim.com" className="text-blue-600 hover:underline">
                    info@airdelayclaim.com
                  </a>
                  . We are committed to resolving all disputes fairly and promptly.
                </p>
              </section>
            </div>

            <div className="mt-12 pt-8 border-t border-gray-200">
              <Link 
                to="/" 
                className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back to Home
              </Link>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default RefundPage;
